import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddOrganization from "../organization/AddOrganization";
import axiosOrganizationInstance from "../organization/AxiosOrganizationInstance";
import { getAuthHeaders } from "../organization/organizationStorage";
import { ORGANIZATION_API_PATHS } from "../constants/apiPaths";
import EatsAdminLayout from "./EatsAdminLayout";
import EatsAdminCreateStepper from "./EatsAdminCreateStepper";
import EatsAdminPageHeader from "./EatsAdminPageHeader";
import RootAdminUserPicker from "./RootAdminUserPicker";
import { sanitizeAadharInput, validateAadharNumber, formatAadharDisplay, formatAadharDisplayOrFallback, sanitizeMobileInput, validateMobileNumber } from "../restaurant/formatDisplay";
import "./EatsAdminCreatePage.css";

const EatsAdminCreateOrganizationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const currentUserId = currentUser?.userId || null;

  const [tab, setTab] = useState("organization");
  const [pendingOrganizations, setPendingOrganizations] = useState([]);
  const [activePendingOrganizationId, setActivePendingOrganizationId] = useState(null);
  const [rootAdminRoleId, setRootAdminRoleId] = useState(null);
  const [rootAdminRoleName, setRootAdminRoleName] = useState("ROOT_ADMIN");
  const [rootAdminForm, setRootAdminForm] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    aadharNo: "",
    password: "",
  });
  const [creatingRootAdmin, setCreatingRootAdmin] = useState(false);
  const [selectedExistingUser, setSelectedExistingUser] = useState(null);
  const [rootAdminMode, setRootAdminMode] = useState("new");

  const activePendingOrganization =
    pendingOrganizations.find(
      (o) => String(o.organizationId) === String(activePendingOrganizationId)
    ) || null;

  const loadPendingOrganizations = async () => {
    try {
      const res = await axiosOrganizationInstance.get("/organization/pending-organizations", {
        headers: getAuthHeaders(),
      });
      const sanitized = Array.isArray(res?.data?.organizations)
        ? res.data.organizations.filter((o) => o && o.organizationId)
        : [];
      setPendingOrganizations(sanitized);
      const requestedId = searchParams.get("organizationId");
      const activeId =
        requestedId &&
        sanitized.some((o) => String(o.organizationId) === String(requestedId))
          ? requestedId
          : sanitized[0]?.organizationId || null;
      setActivePendingOrganizationId(activeId);
      if (activeId) setTab("root-admin");
    } catch (error) {
      console.error(
        "Failed loading pending organizations:",
        error?.response?.data || error?.message
      );
      setPendingOrganizations([]);
      setActivePendingOrganizationId(null);
      setTab("organization");
    }
  };

  useEffect(() => {
    loadPendingOrganizations();
  }, [searchParams]);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const res = await axiosOrganizationInstance.get("/organization/roles", {
          headers: getAuthHeaders(),
        });
        const roles = Array.isArray(res?.data?.roles) ? res.data.roles : [];
        const apiRoot = res?.data?.rootAdminRole;
        if (apiRoot?.Role_Id != null) {
          setRootAdminRoleId(apiRoot.Role_Id);
          setRootAdminRoleName(apiRoot.Role_Name || "ROOT_ADMIN");
          return;
        }
        const rootRole =
          roles.find((r) => String(r.Role_Name || r.roleName || "").toUpperCase() === "ROOT_ADMIN") ||
          roles.find((r) => String(r.Role_Name || r.roleName || "").toUpperCase() === "ADMIN");
        if (rootRole) {
          setRootAdminRoleId(rootRole.Role_Id ?? rootRole.roleId);
          setRootAdminRoleName(rootRole.Role_Name || rootRole.roleName || "ROOT_ADMIN");
        }
      } catch (error) {
        console.error("Failed loading organization roles:", error?.response?.data || error?.message);
      }
    };
    loadRoles();
  }, []);

  const handleOrganizationCreated = (result) => {
    const organization = result?.organization || {};
    if (!organization?.organizationId) {
      alert("Organization created but organizationId was not returned. Please retry.");
      return;
    }
    const payload = {
      organizationId: organization.organizationId,
      organizationName: organization.organizationName || "",
      organizationCode: organization.organizationCode || "",
      createdById: currentUserId,
      createdAt: new Date().toISOString(),
    };
    const nextList = [
      payload,
      ...pendingOrganizations.filter(
        (o) => String(o.organizationId) !== String(payload.organizationId)
      ),
    ];
    setPendingOrganizations(nextList);
    setActivePendingOrganizationId(payload.organizationId);
    setTab("root-admin");
  };

  const selectPendingOrganization = (organizationId) => {
    setActivePendingOrganizationId(organizationId);
    setTab("root-admin");
  };

  const clearPendingOrganization = (organizationId) => {
    const nextList = pendingOrganizations.filter(
      (o) => String(o.organizationId) !== String(organizationId)
    );
    const nextActiveId =
      String(activePendingOrganizationId) === String(organizationId)
        ? nextList[0]?.organizationId || null
        : activePendingOrganizationId;
    setPendingOrganizations(nextList);
    setActivePendingOrganizationId(nextActiveId);
    if (!nextActiveId) setTab("organization");
  };

  const createRootAdminForOrganization = async () => {
    if (!activePendingOrganization?.organizationId) {
      alert("Please create/select an organization first.");
      return;
    }
    if (!currentUserId) {
      alert("Current user is missing. Please sign in again.");
      return;
    }
    if (rootAdminRoleId == null || rootAdminRoleId === "") {
      alert("Organization admin role is not configured. Restart company API and try again.");
      return;
    }

    const assigningExisting = rootAdminMode === "existing" && selectedExistingUser?.userId;
    const { firstName, lastName, mobile, email, aadharNo, password } = rootAdminForm;

    if (assigningExisting) {
      setCreatingRootAdmin(true);
      try {
        const payload = {
          userId: selectedExistingUser.userId,
          roleId: rootAdminRoleId,
          roleName: rootAdminRoleName,
          organizationId: activePendingOrganization.organizationId,
          organizationName: activePendingOrganization.organizationName,
          createdById: currentUserId,
        };
        const response = await axiosOrganizationInstance.post("/organization/users", payload, {
          headers: getAuthHeaders(),
        });
        alert(response?.data?.message || "Root admin assigned successfully");
        setRootAdminForm({ firstName: "", lastName: "", mobile: "", email: "", aadharNo: "", password: "" });
        setSelectedExistingUser(null);
        setRootAdminMode("new");
        clearPendingOrganization(activePendingOrganization.organizationId);
        navigate("/eats/admin?tab=organizations");
      } catch (error) {
        alert(error?.response?.data?.message || "Failed to assign root admin");
      } finally {
        setCreatingRootAdmin(false);
      }
      return;
    }

    if (!firstName || !lastName || !mobile || !email?.trim() || !aadharNo?.trim() || !password) {
      alert("Please fill first name, last name, mobile, email, aadhar number and password.");
      return;
    }
    const mobileError = validateMobileNumber(mobile, { required: true, label: "Mobile" });
    if (mobileError) {
      alert(mobileError);
      return;
    }
    const aadharError = validateAadharNumber(aadharNo);
    if (aadharError) {
      alert(aadharError);
      return;
    }
    setCreatingRootAdmin(true);
    try {
      const payload = {
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        email: email.trim(),
        contactNumber: mobile.trim(),
        aadharNumber: sanitizeAadharInput(aadharNo),
        password,
        roleId: rootAdminRoleId,
        roleName: rootAdminRoleName,
        organizationId: activePendingOrganization.organizationId,
        organizationName: activePendingOrganization.organizationName,
        createdById: currentUserId,
      };
      const response = await axiosOrganizationInstance.post("/organization/users", payload, {
        headers: getAuthHeaders(),
      });
      alert(response?.data?.message || "Root admin user created successfully");
      setRootAdminForm({ firstName: "", lastName: "", mobile: "", email: "", aadharNo: "", password: "" });
      setSelectedExistingUser(null);
      setRootAdminMode("new");
      clearPendingOrganization(activePendingOrganization.organizationId);
      navigate("/eats/admin?tab=organizations");
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to create root admin");
    } finally {
      setCreatingRootAdmin(false);
    }
  };

  const handleSelectExistingRootAdmin = (user) => {
    setSelectedExistingUser(user);
    setRootAdminMode("existing");
    const nameParts = String(user?.name || "").trim().split(/\s+/);
    const firstName = user?.firstName || nameParts[0] || "";
    const lastName = user?.lastName || nameParts.slice(1).join(" ") || "";
    setRootAdminForm({
      firstName,
      lastName,
      mobile: user?.mobile || user?.contactNumber || "",
      email: user?.email || "",
      aadharNo: user?.aadharNo || "",
      password: "",
    });
  };

  const clearExistingRootAdminSelection = () => {
    setSelectedExistingUser(null);
    setRootAdminMode("new");
    setRootAdminForm({ firstName: "", lastName: "", mobile: "", email: "", aadharNo: "", password: "" });
  };

  return (
    <EatsAdminLayout activeTab="organizations">
      <div className="eats-admin-create-page">
        <EatsAdminPageHeader
          title="Create Organization"
          subtitle="Create organization first, then assign root admin user."
          backTo="/eats/admin?tab=organizations"
        />

        <EatsAdminCreateStepper
          activeStepId={tab}
          onStepChange={(next) => {
            if (next === "root-admin" && !activePendingOrganizationId) return;
            setTab(next);
          }}
          steps={[
            { id: "organization", label: "Create Organization" },
            {
              id: "root-admin",
              label: "Create Root Admin",
              disabled: !activePendingOrganizationId,
            },
          ]}
        />

        {pendingOrganizations.length > 0 ? (
          <div className="eats-admin-pending-panel">
            <p className="eats-admin-pending-panel__title">
              Pending organizations ({pendingOrganizations.length}) — select one to continue root admin creation
            </p>
            <div className="eats-admin-pending-grid">
              {pendingOrganizations.map((o) => {
                const isActive = String(o.organizationId) === String(activePendingOrganizationId);
                return (
                  <div
                    key={o.organizationId}
                    className={`eats-admin-pending-card${isActive ? " is-active" : ""}`}
                  >
                    <div className="eats-admin-pending-card__name">
                      {o.organizationName || `Organization ${o.organizationId}`}
                    </div>
                    <div className="eats-admin-pending-card__meta">
                      {o.organizationCode || `ID: ${o.organizationId}`}
                    </div>
                    <div className="eats-admin-pending-card__actions">
                      <button
                        type="button"
                        className={`eats-admin-btn eats-admin-btn--ghost${isActive ? " is-active" : ""}`}
                        onClick={() => selectPendingOrganization(o.organizationId)}
                      >
                        {isActive ? "Selected" : "Use"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="eats-admin-pending-panel__selection">
              Current selection:{" "}
              <strong>
                {activePendingOrganization
                  ? activePendingOrganization.organizationName ||
                    `Organization ${activePendingOrganization.organizationId}`
                  : "None"}
              </strong>
            </div>
          </div>
        ) : null}

        <div className="eats-admin-panel">
          {tab === "organization" ? (
            <AddOrganization
              embedded
              createdById={currentUserId}
              onSuccess={handleOrganizationCreated}
              onClose={() => {}}
            />
          ) : activePendingOrganization ? (
            <div className="eats-admin-root-admin">
              <h3 className="eats-admin-root-admin__title">
                Create Root Admin for{" "}
                {activePendingOrganization.organizationName ||
                  `Organization ${activePendingOrganization.organizationId}`}
              </h3>

              <div className="eats-admin-segmented">
                <button
                  type="button"
                  className={`eats-admin-segmented__btn${rootAdminMode === "new" ? " is-active" : ""}`}
                  onClick={clearExistingRootAdminSelection}
                >
                  Create New User
                </button>
                <button
                  type="button"
                  className={`eats-admin-segmented__btn${rootAdminMode === "existing" ? " is-active" : ""}`}
                  onClick={() => setRootAdminMode("existing")}
                >
                  Assign Existing User
                </button>
              </div>

              {rootAdminMode === "existing" ? (
                <RootAdminUserPicker
                  axiosInstance={axiosOrganizationInstance}
                  apiPath={ORGANIZATION_API_PATHS.organizationRootAdminUsers}
                  selectedUserId={selectedExistingUser?.userId}
                  onSelect={handleSelectExistingRootAdmin}
                  onClear={clearExistingRootAdminSelection}
                />
              ) : null}

              {rootAdminMode === "new" ? (
                <div className="eats-admin-form-grid">
                  <input placeholder="First Name" value={rootAdminForm.firstName} onChange={(e) => setRootAdminForm((p) => ({ ...p, firstName: e.target.value }))} />
                  <input placeholder="Last Name" value={rootAdminForm.lastName} onChange={(e) => setRootAdminForm((p) => ({ ...p, lastName: e.target.value }))} />
                  <input placeholder="Mobile" value={rootAdminForm.mobile} onChange={(e) => setRootAdminForm((p) => ({ ...p, mobile: sanitizeMobileInput(e.target.value) }))} maxLength={10} inputMode="numeric" />
                  <input type="email" placeholder="Email" value={rootAdminForm.email} onChange={(e) => setRootAdminForm((p) => ({ ...p, email: e.target.value }))} />
                  <input placeholder="Aadhar Number" value={formatAadharDisplay(rootAdminForm.aadharNo)} onChange={(e) => setRootAdminForm((p) => ({ ...p, aadharNo: sanitizeAadharInput(e.target.value) }))} maxLength={14} inputMode="numeric" />
                  <input type="password" placeholder="Password" value={rootAdminForm.password} onChange={(e) => setRootAdminForm((p) => ({ ...p, password: e.target.value }))} />
                </div>
              ) : selectedExistingUser ? (
                <div className="eats-admin-selected-user">
                  Selected: <strong>{selectedExistingUser.name || `${rootAdminForm.firstName} ${rootAdminForm.lastName}`.trim()}</strong>
                  {" · "}Aadhar: <strong>{formatAadharDisplayOrFallback(selectedExistingUser.aadharNo || rootAdminForm.aadharNo)}</strong>
                  {" · "}Mobile: <strong>{selectedExistingUser.mobile || rootAdminForm.mobile || "—"}</strong>
                </div>
              ) : (
                <div className="eats-admin-hint">
                  Search by Aadhar number above and select a user to assign.
                </div>
              )}

              <div className="eats-admin-actions">
                <button
                  type="button"
                  className="eats-admin-btn eats-admin-btn--primary"
                  onClick={createRootAdminForOrganization}
                  disabled={creatingRootAdmin || (rootAdminMode === "existing" && !selectedExistingUser?.userId)}
                >
                  {creatingRootAdmin ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <div className="eats-admin-empty-state">Please create/select an organization first.</div>
          )}
        </div>
      </div>
    </EatsAdminLayout>
  );
};

export default EatsAdminCreateOrganizationPage;
